import { z } from 'zod';

/**
 * What someone can own in PM, in the order the ownership screen lists it.
 * Ownership is authority here - a private board opens to its creator, a
 * workflow acts with its owner's rights - so a deactivated account would
 * otherwise strand all of it.
 */
export const ownedKinds = [
  'organizations', 'workspaces', 'boards', 'boardOwnerRoles', 'docs', 'dashboards', 'workflows', 'forms',
  'boardLinks', 'templates', 'folders', 'departmentLeads', 'invites',
] as const;
export type OwnedKind = (typeof ownedKinds)[number];
export type OwnedCounts = Partial<Record<OwnedKind, number>>;

/** Hand everything a deactivated account owns in PM to the person taking over. */
export const TransferOwnership = z.object({ toUserId: z.string().uuid() });
export type TransferOwnership = z.infer<typeof TransferOwnership>;

/**
 * One of the leaver's own private boards, docs or dashboards that stays where it is,
 * because the successor was never invited to it. The name is only there for
 * someone who can open it.
 */
export interface KeptPrivate {
  kind: 'board' | 'doc' | 'dashboard';
  id: string;
  name: string | null;
}

/** GET /employees/:userId/pm-ownership */
export interface OwnershipSummary {
  user: { id: string; name: string; isActive: boolean };
  /** What they own now, zeros left out. */
  owned: OwnedCounts;
  /** What stays even so, because the person asking may not hand it out. */
  heldBack: OwnedCounts;
  /**
   * Their private boards and docs that would stay: with a successor named,
   * exactly those that successor is not invited to; without one, all of them.
   */
  keptPrivate: KeptPrivate[];
}

/** POST /employees/:userId/pm-ownership/transfer */
export interface OwnershipTransferResult {
  moved: OwnedCounts;
  /** Of what moved, the workflows and board links that were on and are now off. */
  switchedOff: OwnedCounts;
  /** What stayed because the person who ran it may not hand it out. */
  heldBack: OwnedCounts;
  keptPrivate: KeptPrivate[];
}
